import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { Locale } from "@porsche-customer/slfinrtl-context-types"
import {
	PCheckbox,
	PHeading,
	PIcon,
	PTable,
	PTableBody,
	PTableCell,
	PTableHead,
	PTableHeadCell,
	PTableHeadRow,
	PTableRow,
	PText,
} from "@porsche-design-system/components-react"
import { spacingStaticXs, spacingStaticLg, spacingStaticXl } from "@porsche-design-system/components-react/emotion"
import type { Equipment } from "@slfinrtl/ui-bff-routes"
import { useCallback } from "react"
import { FormattedMessage } from "react-intl"
import { equipmentListMessages } from "./equipmentList.messages"

interface EquipmentListProps {
	items: Equipment[]
	onEquipmentToggled: (code: string) => void
	selectedEquipment: string[]
	selectedLocale: Locale
}

export const EquipmentList = (props: EquipmentListProps) => {
	const { items, onEquipmentToggled, selectedEquipment, selectedLocale, ...rest } = props
	const { locale } = useDealerContext()

	const isEquipmentChecked = useCallback(
		(code: string) => {
			return selectedEquipment.includes(code)
		},
		[selectedEquipment],
	)

	return (
		<>
			<PTable {...rest} caption="Equipment List">
				<PTableHead>
					<PTableHeadRow>
						<PTableHeadCell>
							<FormattedMessage {...equipmentListMessages.headerVisible} />
						</PTableHeadCell>
						<PTableHeadCell>
							<FormattedMessage {...equipmentListMessages.headerName} />
						</PTableHeadCell>
						<PTableHeadCell>
							<FormattedMessage {...equipmentListMessages.headerCode} />
						</PTableHeadCell>
						<PTableHeadCell>
							<FormattedMessage {...equipmentListMessages.headerCategory} />
						</PTableHeadCell>
					</PTableHeadRow>
				</PTableHead>
				<PTableBody>
					{items.map((item) => {
						const isTranslationMissing = !item.name?.[selectedLocale]
						return (
							<StyledTableRow key={item.code} onClick={() => onEquipmentToggled(item.code)}>
								<PTableCell>
									<PCheckbox checked={isEquipmentChecked(item.code)} />
								</PTableCell>
								<PTableCell>
									{isTranslationMissing ? (
										<IconWrapper>
											<PIcon color="error" size="sm" name="warning" />
											<PText color="error">{item.name[locale]}</PText>
										</IconWrapper>
									) : (
										<PText>{item.name[locale]}</PText>
									)}
								</PTableCell>
								<PTableCell>{item.code}</PTableCell>
								<PTableCell>{item.categoryName}</PTableCell>
							</StyledTableRow>
						)
					})}
				</PTableBody>
			</PTable>
			{items.length === 0 && (
				<NoResultsWrapper>
					<PHeading size="lg" align="center">
						<FormattedMessage {...equipmentListMessages.noResult} />
					</PHeading>
				</NoResultsWrapper>
			)}
		</>
	)
}

const IconWrapper = styled.div`
	display: flex;
	gap: ${spacingStaticXs};
`

const StyledTableRow = styled(PTableRow)`
	cursor: pointer;
`

const NoResultsWrapper = styled.div`
	margin: ${spacingStaticXl} auto ${spacingStaticLg};
`
